import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, IconButton } from '@mui/material';
import './subscriptionModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faSpinner, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { navigateExternal } from '../utils/navigation.js';

function BillingCycleToggle({ billingCycle, setBillingCycle }) {
  const { t } = useTranslation();
  return (
    <div className="billing-toggle" data-cycle={billingCycle}>
      <span className="billing-thumb" aria-hidden />
      <button
        type="button"
        className={billingCycle === "month" ? "active" : ""}
        onClick={() => setBillingCycle("month")}
      >
        {t('monthly')}
      </button>
      <button
        type="button"
        className={billingCycle === "year" ? "active" : ""}
        onClick={() => setBillingCycle("year")}
      >
        {t('yearly')}
      </button>
    </div>
  );
}

function PriceSwap({ showYearly, yearly, monthly }) {
  const prefersReduced = typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div className={`price-swap ${prefersReduced ? "no-anim" : ""}`}>
      <div className={`pane ${showYearly ? "show" : ""}`} aria-hidden={!showYearly}>
        {yearly}
      </div>
      <div className={`pane ${!showYearly ? "show" : ""}`} aria-hidden={showYearly}>
        {monthly}
      </div>
    </div>
  );
}

const StripeSubscriptionModal = ({ open, onClose, bot }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(1);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [billingCycle, setBillingCycle] = useState("month");
  const wrapperRef = useRef();
  const cardRefs = useRef([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}subscription_plans`);
        const data = await res.json();
        // Store all plans (both monthly and yearly)
        const sorted = data.sort((a, b) => a.monthly_price - b.monthly_price);
        setPlans(sorted);
      } catch (err) {
        console.error(err);
        setError(t('billing_plans_load_error'));
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Show only monthly plans (we'll swap prices based on billing cycle)
  const monthlyPlans = useMemo(() => {
    return plans.filter(plan => plan.billing_interval === 'month');
  }, [plans]);

  // Map yearly plans by name for price lookup
  const yearlyPlanMap = useMemo(() => {
    const map = {};
    plans.filter(p => p.billing_interval === 'year').forEach(p => {
      map[p.name] = p;
    });
    return map;
  }, [plans]);

  // Calculate savings (both monthly_price fields are monthly prices)
  const savingsMap = useMemo(() => {
    const map = {};
    const monthlyList = plans.filter(p => p.billing_interval === 'month');
    const yearlyList = plans.filter(p => p.billing_interval === 'year');

    yearlyList.forEach(yearlyPlan => {
      const monthlyPlan = monthlyList.find(m => m.name === yearlyPlan.name);
      if (monthlyPlan && monthlyPlan.monthly_price > 0) {
        // Both are monthly prices, so compare directly
        const savings = Math.round(((monthlyPlan.monthly_price - yearlyPlan.monthly_price) / monthlyPlan.monthly_price) * 100);
        map[yearlyPlan.name] = {
          savingsPercent: savings,
          yearlyMonthlyPrice: yearlyPlan.monthly_price,
          originalMonthly: monthlyPlan.monthly_price
        };
      }
    });
    return map;
  }, [plans]);

  // Initialize scroll position to show only one arrow
  useEffect(() => {
    if (!open || monthlyPlans.length === 0) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    setTimeout(() => {
      // Find current plan index
      const currentPlanIndex = monthlyPlans.findIndex(
        (plan) => String(plan.id) === String(bot.subscription_plan?.id)
      );

      const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;

      // If current plan is the last one, scroll to max right (show left arrow only)
      // Otherwise, scroll to far left (show right arrow only)
      if (currentPlanIndex === monthlyPlans.length - 1) {
        wrapper.scrollLeft = maxScroll;
        setActiveIndex(monthlyPlans.length - 1);
      } else {
        wrapper.scrollLeft = 0;
        setActiveIndex(0);
      }
    }, 50);
  }, [open, monthlyPlans, bot.subscription_plan?.id]);

  // Handle scroll to update active indicator and scroll arrows
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleScroll = () => {
      const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;

      // Update scroll arrow visibility
      setCanScrollLeft(wrapper.scrollLeft > 5);
      setCanScrollRight(wrapper.scrollLeft < maxScroll - 5);

      // If at the very start, highlight first indicator
      if (wrapper.scrollLeft <= 5) {
        setActiveIndex(0);
        return;
      }

      // If at the very end, highlight last indicator
      if (wrapper.scrollLeft >= maxScroll - 5) {
        setActiveIndex(monthlyPlans.length - 1);
        return;
      }

      // Otherwise, find the closest card to viewport center
      const wrapperRect = wrapper.getBoundingClientRect();
      const viewportCenter = wrapperRect.left + wrapperRect.width / 2;
      let closestIdx = 0;
      let smallestDist = Infinity;

      cardRefs.current.forEach((card, idx) => {
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const dist = Math.abs(viewportCenter - cardCenter);
        if (dist < smallestDist) {
          smallestDist = dist;
          closestIdx = idx;
        }
      });
      setActiveIndex(closestIdx);
    };

    handleScroll(); // Initial check
    wrapper.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [monthlyPlans]);

  const scrollLeft = () => {
    const wrapper = wrapperRef.current;
    if (wrapper) {
      const newPosition = Math.max(0, wrapper.scrollLeft - 340);
      // If we'd be close to start, just go to 0
      wrapper.scrollTo({ left: newPosition < 50 ? 0 : newPosition, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const wrapper = wrapperRef.current;
    if (wrapper) {
      const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
      const newPosition = wrapper.scrollLeft + 340;
      // If we'd be close to end, just go to max
      wrapper.scrollTo({ left: newPosition > maxScroll - 50 ? maxScroll : newPosition, behavior: 'smooth' });
    }
  };

  const scrollToCard = (index) => {
    const wrapper = wrapperRef.current;
    const card = cardRefs.current[index];
    if (wrapper && card) {
      const left = card.offsetLeft - wrapper.offsetWidth / 2 + card.offsetWidth / 2;
      wrapper.scrollTo({ left, behavior: 'smooth' });
    }
  };

  const handlePlanSelect = async (plan) => {
    if (String(plan.id) === String(bot.subscription_plan?.id)) return
    setIsProcessing(true)

    if (window.dataLayer) {
      window.dataLayer.push({ event: 'checkout_session_started' });
    }

    try {
      const currentPlan = bot.subscription_plan
      const action =
        !currentPlan || currentPlan.name === 'Free'
          ? 'Start Free Trial'
          : plan.monthly_price > currentPlan?.monthly_price
          ? 'Upgrade'
          : 'Modify'

      if (action === 'Start Free Trial') {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}create-checkout-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bot_id: bot.id,
            price_id: plan.stripe_subscription_plan_id,
            subscription_plan_id: plan.id,
            coupon_id: plan.coupon_id
          })
        })
        const data = await res.json()
        if (data?.checkout_url) navigateExternal(data.checkout_url)
        else alert(t('billing_checkout_session_error'))
      } else if (['Upgrade', 'Cancel', 'Modify'].includes(action)) {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}create-customer-portal-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            stripe_customer_id: bot.subscription.stripe_customer_id
          })
        })
        const data = await res.json()
        if (data?.url) navigateExternal(data.url)
        else alert(t('billing_portal_session_error'))
      } else alert(t('billing_nothing_to_change'))
    } catch (err) {
      console.error(err)
      alert(t('something_went_wrong'))
    } finally {
      setIsProcessing(false)
    }
  }

  // Helper function to build feature list from plan data
  const buildFeatures = (plan) => {
    const features = [
      {
        text: plan.name === 'Free'
          ? t('billing_feat_messages_onetime', { count: plan.message_limit.toLocaleString('de-CH') })
          : t('billing_feat_messages', { count: plan.message_limit.toLocaleString('de-CH') }),
        included: true
      },
      {
        text: t('billing_feat_knowledge_sources', { count: plan.document_limit.toLocaleString('de-CH') }),
        included: true
      },
      {
        text: t('billing_feat_skills', { count: plan.skill_limit ?? 0 }),
        included: true
      },
      {
        text: t('billing_feat_custom_behaviors', { count: plan.behavior_limit }),
        included: true
      },
      {
        text: t('billing_feat_auto_lead_capture'),
        included: true
      },
      {
        text: plan.automatic_rescraping === 'none'
          ? t('billing_feat_rescraping')
          : plan.automatic_rescraping === 'daily'
          ? t('billing_feat_rescraping_daily')
          : t('billing_feat_rescraping_monthly'),
        included: plan.automatic_rescraping !== 'none'
      },
      {
        text: t('billing_feat_remove_branding'),
        included: plan.remove_branding
      }
    ];
    return features;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: ['20px', '25px', '25px'],
          paddingTop: '20px !important',
          width: ['90%', '85%', '1100px'],
          maxWidth: ['85%', 'none', 'none'],
          maxHeight: '90vh',
          overflowY: 'auto',
          outline: 'none'
        }}
      >
        {loading && (
          <div className="loading-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '48px', color: '#5e1bff' }} />
            <p style={{ marginTop: '16px', color: '#333', fontWeight: 600 }}>{t('billing_loading_plans')}</p>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}

        {!loading && !error && (
          <div className="modal-content">
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 20,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
              aria-label={t('close')}
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>

            <div className="trial-info-text">{t('billing_trial_info')}</div>

            <BillingCycleToggle billingCycle={billingCycle} setBillingCycle={setBillingCycle} />

            <div style={{ position: 'relative' }}>
              <button
                className={`scroll-arrow left ${!canScrollLeft ? 'hidden' : ''}`}
                onClick={scrollLeft}
                aria-label={t('billing_scroll_left')}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className="plan-slider-wrapper" ref={wrapperRef} style={{ overflowX: 'auto', overflowY: 'visible', paddingTop: '0' }}>
                <div className="plan-slider" style={{ display: 'flex', flexWrap: 'nowrap', width: 'max-content', marginTop: '0' }}>
                  {monthlyPlans.map((plan, i) => {
                  const yearlyPlan = yearlyPlanMap[plan.name];
                  const isCurrentPlan = String(plan.id) === String(bot.subscription_plan?.id) ||
                    (yearlyPlan && String(yearlyPlan.id) === String(bot.subscription_plan?.id));
                  const isHighlighted = plan.name === 'Boost';
                  const features = buildFeatures(plan);
                  const savings = savingsMap[plan.name];
                  const isYearly = billingCycle === 'year';
                  // Use yearly plan for checkout if yearly is selected
                  const selectedPlan = isYearly && yearlyPlan ? yearlyPlan : plan;

                  return (
                    <article
                      key={plan.id}
                      ref={(el) => (cardRefs.current[i] = el)}
                      className={`plan-card ${isHighlighted ? 'highlighted' : ''}`}
                      style={{ flex: '0 0 320px', width: '320px', minWidth: '320px' }}
                    >
                      <h3>{plan.name}</h3>
                      <div className="price-section">
                        <PriceSwap
                          showYearly={isYearly}
                          yearly={
                            savings ? (
                              <>
                                <div className="yearly-crossout">
                                  <span className="old-price">${savings.originalMonthly}/mo</span>
                                  <span className="arrow">→</span>
                                  <span className="new-price">${savings.yearlyMonthlyPrice}/mo</span>
                                  <span className="deal-tag">-{savings.savingsPercent}%</span>
                                </div>
                                <p className="per-month">{t('billing_billed_annually')}</p>
                              </>
                            ) : (
                              <>
                                <p className="plan-price">${yearlyPlan?.monthly_price || plan.monthly_price}/mo</p>
                                <p className="per-month">{t('billing_billed_annually')}</p>
                              </>
                            )
                          }
                          monthly={
                            plan.trial_period_months > 0 ? (
                              <>
                                <p className="plan-price">${plan.trial_price}</p>
                                <p className="per-month">{t('billing_trial_per_month_for', { count: plan.trial_period_months })}</p>
                                <p className="after-trial">{t('billing_after_trial_price', { price: plan.monthly_price })}</p>
                              </>
                            ) : (
                              <>
                                <p className="plan-price">${plan.monthly_price}</p>
                                <p className="per-month">{t('per_month')}</p>
                              </>
                            )
                          }
                        />
                      </div>
                      <ul className="plan-features">
                        {features.map((f, idx) => (
                          <li key={idx}>
                            <FontAwesomeIcon
                              icon={f.included ? faCheck : faXmark}
                              className={f.included ? 'plan-icons-included' : 'plan-icons-not-included'}
                            />
                            {f.text}
                          </li>
                        ))}
                      </ul>
                      <button
                        className="plan-button"
                        disabled={isProcessing || isCurrentPlan}
                        onClick={() => handlePlanSelect(selectedPlan)}
                      >
                        {isProcessing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                            {t('billing_processing')}
                          </>
                        ) : isCurrentPlan ? (
                          t('current_plan')
                        ) : (
                          t('start_free_trial')
                        )}
                      </button>
                    </article>
                  );
                })}
              </div>
            </div>

              <button
                className={`scroll-arrow right ${!canScrollRight ? 'hidden' : ''}`}
                onClick={scrollRight}
                aria-label={t('billing_scroll_right')}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>

            <div className="pricing-indicators">
              {monthlyPlans.map((_, i) => (
                <div
                  key={i}
                  className={`indicator ${i === activeIndex ? 'active' : ''}`}
                  onClick={() => scrollToCard(i)}
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  aria-label={t('billing_go_to_plan', { number: i + 1 })}
                />
              ))}
            </div>
          </div>
        )}
      </Box>
    </Modal>
  )
}

export default StripeSubscriptionModal
